import { TFunction } from 'i18next';

// How a server refusal on the invoice screens becomes the line the operator
// reads — one place, shared by the invoice section and the recipient check.

/**
 * Refusals whose `detail` is a comma-separated list of the missing party
 * fields. Only these get their detail shown: for other reasons `detail` is an
 * internal value (a mode, a status, an S3 error) and stays off the screen.
 */
const FIELD_LIST_REASONS = new Set(['seller_incomplete', 'buyer_address_incomplete', 'einvoice_incomplete']);

type RefusalBody = { message?: string, reason?: string, code?: string, detail?: string };

/**
 * The server's reason, in the operator's language. Every refusal from these
 * endpoints carries a `reason` (the legacy-store refusal a `code`), and each
 * has its own text under `account.invoice_refusal` — the server's `message` is
 * English, written for whoever reads the API, and reaches the screen only for
 * a reason this list does not know yet. For a missing-data refusal the fields
 * it names are what the user has to fill in, so they follow.
 */
export const messageOf = (err: unknown, t: TFunction): string | undefined => {
    const data = (err as { data?: RefusalBody })?.data;
    const reason = data?.reason ?? data?.code;
    if (!reason) return data?.message;
    const lead = t(`account.invoice_refusal.${reason}`, { defaultValue: data?.message ?? '' }) || undefined;
    if (!FIELD_LIST_REASONS.has(reason) || !data?.detail) return lead;
    const fields = data.detail
        .split(',')
        .map((field) => field.trim())
        .filter(Boolean)
        .map((field) => t(`account.invoice_field.${field}`, { defaultValue: field }));
    const missing = t('account.invoice_missing_fields', { fields: fields.join(', ') });
    return lead ? `${lead} ${missing}` : missing;
};
